import { useEffect, useState, useCallback, type ReactElement } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type {
  DimensionScoreRecord,
  MaturityLevel,
  DiscoveryFeatureDetailView,
  DiscoveryRecommendation,
  Recommendation,
} from '@sdd-maturity/shared-types';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../lib/api-client';
import { DimensionCard } from '../components/DimensionCard';
import { DimensionRubricDialog } from '../components/DimensionRubricDialog';
import { DIMENSION_WEIGHT_PERCENT } from '../lib/rubrics';
import { DiscoveryScanProgress } from '../components/DiscoveryScanProgress';
import { ScanFooterLine } from '../components/ScanFooterLine';
import { RescanFooter } from '../components/RescanFooter';
import { FrameworkBadge } from '../components/FrameworkBadge';
import { useRubricDisclosure } from '../hooks/useRubricDisclosure';
import { useRescan } from '../hooks/useRescan';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { scoreColor, levelLabel, levelBadgeClass } from '../lib/score';
import { compareRecommendations } from '../lib/recommendations';
import { RecommendationCard } from '../components/RecommendationCard';
import { RecommendationsList } from '../components/RecommendationsList';

interface DiscoveryFinding {
  type?: 'gap' | 'strength';
  severity?: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  status?: 'open' | 'resolved';
}

interface DiscoveryDimRaw {
  dimension?: string;
  name?: string;
  /** Findings — structured objects for new records, plain strings for legacy records */
  findings?: Array<string | DiscoveryFinding>;
  /**
   * Recommendation — structured `{ title, description, ... }` for d-v2 records; a
   * plain string for legacy `d-v1` records (mirrors the `findings` union). The
   * structured arm derives from the shared `DiscoveryRecommendation` (no inline
   * re-declaration): `title`/`description` are required; `effort`/`impact` stay
   * optional here (`Partial`) because legacy structured records may omit them
   * and this page falls back to the dimension-level siblings.
   */
  recommendation?:
    | string
    | (Pick<DiscoveryRecommendation, 'title' | 'description'> &
        Partial<Pick<DiscoveryRecommendation, 'effort' | 'impact'>>);
  effort?: 'low' | 'medium' | 'high';
  impact?: 'low' | 'medium' | 'high';
}

// Shares the repo page's --genai-sem-chart-series-* token palette (tokens.css light,
// theme.css dark) so the discovery-dimension trend lines read consistently with the
// repo Score History chart and track the Interstate brand palette in both themes,
// rather than a standalone Tailwind-default palette. Order mirrors ScoreHistoryChart's
// series-1..6 assignment.
const DIM_COLORS: Record<string, string> = {
  'Discovery Planning Maturity': 'var(--genai-sem-chart-series-1)',
  'Problem Framing Quality': 'var(--genai-sem-chart-series-2)',
  'Hypothesis Validation Rigor': 'var(--genai-sem-chart-series-3)',
  'Solution Option Exploration': 'var(--genai-sem-chart-series-4)',
  'User Validation Depth': 'var(--genai-sem-chart-series-5)',
  'Discovery-to-Delivery Handoff': 'var(--genai-sem-chart-series-6)',
};

/**
 * Discovery dimensions temporarily out of scoring — mirrors the `scored: false`
 * entries in `lambdas/bedrock-evaluator/src/discovery-rubric.ts`. No reliable
 * source artifacts exist today (hypothesis logs, alternatives-considered
 * sections, user-research notes), so Bedrock isn't asked to score them and the
 * dashboard shows "Coming Soon" instead of a fabricated score. Revert both
 * sides together when reliable artifacts become available.
 */
const COMING_SOON_DISCOVERY_DIMENSIONS = [
  'Hypothesis Validation Rigor',
  'Solution Option Exploration',
  'User Validation Depth',
];

/**
 * Adapt Discovery's `DiscoveryRecommendation` to the shared `RecommendationsList`
 * prop (Delivery's `Recommendation`). `RecommendationsList` only *reads*
 * title/description/effort/impact, the `dimension` (rendered as the right-aligned
 * tag) and `priority` (dedup + sort tie-break). A grouped Discovery rec is
 * cross-dimension, so it carries no single `dimension` tag and no Delivery
 * `category` taxonomy — those Delivery-only required fields are not read for this
 * render, so no value is fabricated as displayed data (the tag renders empty
 * rather than a made-up dimension). `priority` mirrors the already-ranked
 * (grouped + capped) position: `RecommendationsList` re-applies the SAME
 * `compareRecommendations` comparator, so the order is preserved (idempotent).
 * `rootCause`/`examples` flow through unchanged so the single-sourced shape is
 * carried without drift.
 */
function toSharedRecommendations(recs: DiscoveryRecommendation[]): Recommendation[] {
  return recs.map((rec, index) => ({
    dimension: '', // cross-dimension grouped rec → empty tag, not a fabricated dimension
    title: rec.title,
    description: rec.description,
    effort: rec.effort,
    impact: rec.impact,
    priority: index,
    ...(rec.rootCause !== undefined ? { rootCause: rec.rootCause } : {}),
    ...(rec.examples !== undefined ? { examples: rec.examples } : {}),
  })) as unknown as Recommendation[];
}

function RecommendationsPanel({
  recommendations,
  dims,
}: {
  recommendations?: DiscoveryRecommendation[];
  dims: DiscoveryDimRaw[];
}): ReactElement {
  // Prefer the top-level grouped + capped recommendations[] (Slice 2 consolidation),
  // rendered through Delivery's SHARED RecommendationsList so Discovery and Delivery
  // render identically — no Discovery-specific card assembly. An empty top-level
  // array is still "present": it renders the shared list's own empty state.
  if (recommendations) {
    return <RecommendationsList recommendations={toSharedRecommendations(recommendations)} />;
  }

  // Legacy fallback — reached ONLY when the top-level array is absent. Legacy d-v1
  // string recs + d-v2 ungrouped structured recs still render per-dimension via the
  // shared RecommendationCard (back-compat).
  const recs = dims
    .filter((d) => d.recommendation)
    .map((d) => {
      const rec = d.recommendation!;
      const structured = typeof rec === 'object';
      return {
        dimName: d.dimension ?? d.name ?? '',
        // d-v2 structured rec → title + description; legacy string → title only.
        title: structured ? rec.title : rec,
        description: structured ? rec.description : '',
        effort: structured ? (rec.effort ?? d.effort) : d.effort,
        impact: structured ? (rec.impact ?? d.impact) : d.impact,
      };
    })
    // Effort-weighted desirability (high impact + low effort first); see lib/recommendations.ts.
    .sort(compareRecommendations)
    // Number badge reflects sorted position, not dimension order.
    .map((r, i) => ({ ...r, idx: i + 1 }));

  return (
    <div data-testid="discovery-recommendations">
      <h3 className="text-lg font-semibold text-primary mb-md">
        Top Recommendations
      </h3>
      {recs.length === 0 ? (
        <p className="text-secondary">No recommendations available.</p>
      ) : (
        <div className="card rec-group">
          {recs.map((r) => (
            // Shared card — renders identically to Delivery's RecommendationsList.
            <RecommendationCard
              key={r.dimName}
              index={r.idx}
              title={r.title}
              description={r.description}
              effort={r.effort}
              impact={r.impact}
              tag={r.dimName}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Feature drilldown page for Discovery-scored Rally features.
 * AC-8.1: 6 DimensionCards for the 6 Discovery dimensions.
 * AC-8.2: Prominent Rally UILink with external-link icon.
 * AC-8.3: redirect to /teams/{teamNumber} if not-sdd (bucket kept internal-only, no badge).
 * AC-8.4 removed: the caveat banner (both its "Partial SDD" and "Low Confidence" wordings)
 * fired on the exact same condition (lowConfidence is derived 1:1 from bucket === 'partial-sdd'
 * in bedrock-evaluator) and wasn't actionable either way.
 */
export function FeatureDrillDown() {
  const { formattedId, teamNumber: pathTeam } = useParams<{ formattedId: string; teamNumber: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const id = formattedId ?? '';
  // FR-5: owning team comes from the canonical path (`/teams/:teamNumber/discovery/
  // features/:formattedId`) — the URL is the single source of truth, no `?team=`.
  const teamNumber = pathTeam ?? undefined;
  const teamName = teamNumber;
  const isAdmin = useIsAdmin();

  // Per-feature re-scan — mirrors the Delivery RepoDrillDown re-scan button.
  // Triggers a Discovery scan scoped to just this feature (formattedIds=[id])
  // and watches progress inline. Scan state is keyed in sessionStorage so an
  // in-flight scan survives a reload. Respects the backend's 24h idempotency
  // gate: a recent team scan returns 409 SCAN_RECENTLY_COMPLETED, surfaced as
  // a notice rather than an error (no force flag sent).
  // Shared re-scan state machine. The 409 SCAN_RECENTLY_COMPLETED case (the team
  // was scanned within the last 24h) is surfaced as an informational notice rather
  // than a hard error — opt-in via recentlyCompletedNotice.
  const scanKey = `sdd-discovery-scan-${teamNumber ?? ''}/${id}`;
  const { rescanArn, rescanning, rescanError, rescanNotice, start, handleRescanComplete } = useRescan(
    scanKey,
    { recentlyCompletedNotice: 'This team was scanned within the last 24 hours — try again later.' },
  );
  const { activeDimension, openRubric, closeRubric } = useRubricDisclosure();

  const handleRescan = useCallback(() => {
    if (!token || !id || !teamNumber || rescanning) return;
    void start(() => apiClient.triggerDiscoveryScan(teamNumber, token, teamName, [id]));
  }, [token, id, teamNumber, teamName, rescanning, start]);

  const { data: feature, isLoading, error } = useApi(
    (client, token) => client.getDiscoveryFeature(id, token, teamNumber),
    [id, teamNumber],
  );

  const { data: scanHistory } = useApi<DiscoveryFeatureDetailView[]>(
    (client, token) =>
      id && teamNumber
        ? client.getDiscoveryFeatureHistory(id, teamNumber, token)
        : Promise.resolve([]),
    [id, teamNumber],
  );

  // AC-8.3: Redirect not-sdd features back to team page
  useEffect(() => {
    if (feature && feature.bucket === 'not-sdd' && feature.teamNumber) {
      navigate(`/teams/${feature.teamNumber}`, { replace: true });
    }
  }, [feature, navigate]);

  if (isLoading) {
    return (
      <div data-testid="feature-drilldown-loading">
        <h2 className="text-2xl font-bold mb-md">Feature Details</h2>
        <p className="text-secondary">Loading feature details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="feature-drilldown-error">
        <h2 className="text-2xl font-bold mb-md">Feature Details</h2>
        <div className="card" style={{ borderColor: 'var(--genai-sem-error)' }}>
          <p className="text-lg font-semibold" style={{ color: 'var(--genai-sem-error)' }}>
            Failed to load feature
          </p>
          <p className="text-secondary mt-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!feature) return null;

  // Map discovery dimensions to DimensionScoreRecord shape
  // Bedrock returns level as numeric 1-5; normalize to 'L1'-'L5' for DimensionCard badges.
  // Guard with Array.isArray — scoring-error records persist dimensions as {} not [].
  const safeDims = Array.isArray(feature.dimensions) ? feature.dimensions : [];
  const dimensionRecords: DimensionScoreRecord[] = safeDims.map((d) => {
    const rawLevel = String(d.level ?? '');
    const level: MaturityLevel = /^[1-5]$/.test(rawLevel)
      ? `L${rawLevel}` as MaturityLevel
      : /^L[1-5]$/i.test(rawLevel)
        ? rawLevel.toUpperCase() as MaturityLevel
        : 'L1';
    return {
      dimension: (d.dimension ?? d.name ?? 'constitution-maturity') as DimensionScoreRecord['dimension'],
      score: d.score,
      level,
      confidence: d.confidence ?? 0,
    };
  });

  // Dimensions temporarily out of scoring (discovery-rubric.ts's `scored: false`)
  // — no reliable source artifacts today. Appended as placeholders only when
  // absent from the persisted record, so a pre-existing score cached from
  // before this change (still carrying all 6) keeps showing its real value.
  const scoredDimensionNames = new Set(dimensionRecords.map((r) => r.dimension));
  const comingSoonRecords: DimensionScoreRecord[] = COMING_SOON_DISCOVERY_DIMENSIONS.filter(
    (name) => !scoredDimensionNames.has(name as DimensionScoreRecord['dimension'])
  ).map((name) => ({
    dimension: name as DimensionScoreRecord['dimension'],
    score: 0,
    level: 'L1',
    confidence: 0,
  }));
  const allDimensionRecords = [...dimensionRecords, ...comingSoonRecords];
  const comingSoonDimensionSet = new Set(COMING_SOON_DISCOVERY_DIMENSIONS);

  // Cast to runtime shape (API returns structured findings, shared-type has them as string[])
  const dimsRaw = (Array.isArray(feature.dimensions) ? feature.dimensions : []) as unknown as DiscoveryDimRaw[];

  // Observed framework provenance (discovery-framework-from-rally-tag AC-4.2).
  // The detail endpoint returns the raw record, so it carries classification.framework;
  // prefer a top-level framework mirror when present.
  const framework = feature.framework ?? feature.classification?.framework;

  const overallScore = feature.overallScore ?? 0;
  const overallLevel = String(feature.overallLevel ?? feature.level ?? '');
  const normalizedLevel = /^[1-5]$/.test(overallLevel) ? `L${overallLevel}` : overallLevel;
  const scanTimestamp = String(feature.scanTimestamp ?? feature.timestamp ?? '');

  const scanHistoryArr = Array.isArray(scanHistory) ? scanHistory : [];
  const chartData = scanHistoryArr.map(scan => {
    const rawScan = scan as unknown as Record<string, unknown>;
    // SDM-o96r: scoring-error / SKIPPED rows persist `dimensions` as an empty
    // map ({}) instead of a list ([]). Without the Array.isArray guard the
    // .forEach below throws and blanks the page (see also lines 383, 400).
    const rawDims = rawScan['dimensions'];
    const dims = (Array.isArray(rawDims) ? rawDims : []) as Array<{ dimension?: string; score?: number }>;
    const point: Record<string, unknown> = {
      date: new Date(String(rawScan['scanTimestamp'] ?? scan.scanTimestamp ?? rawScan['timestamp'] ?? scan.timestamp ?? '')).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
      overall: Number(rawScan['overallScore'] ?? scan.overallScore ?? 0),
    };
    dims.forEach(d => {
      if (d.dimension) point[d.dimension] = d.score ?? 0;
    });
    return point;
  }).reverse(); // oldest first for left-to-right trend

  return (
    <div data-testid="feature-drilldown">
      {/* Location + up-navigation now live in the node-scoped page-header breadcrumb
          (FR-3); the old inline "Discovery / team / feature" nav was a duplicate. */}

      {/* Header — mirrors Delivery RepoDrillDown: title left, score+level right.
          mb-lg matches the section spacing used below (and on RepoDrillDown). */}
      <div className="flex justify-between items-start mb-lg" style={{ flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div className="flex items-center gap-md mb-sm" style={{ flexWrap: 'wrap' }}>
            <h2 className="text-2xl font-bold" style={{ margin: 0 }}>{feature.name ?? feature.formattedId}</h2>
            {framework && framework !== 'none' && framework !== 'unknown' ? (
              <FrameworkBadge framework={framework} />
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
          <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--genai-text-base)' }}>
            {feature.name && <span>{feature.formattedId}</span>}
            {feature.rallyLink && (
              <>
                {feature.name && ' '}
                <a
                  href={feature.rallyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="rally-link"
                  className="btn-link"
                  aria-label={`Open ${feature.formattedId} in Rally (opens in new tab)`}
                >
                  Rally ↗
                </a>
              </>
            )}
          </p>
        </div>
        {/* Score + level badge — matches Delivery's top-right score display */}
        <div className="flex items-center gap-sm">
          {isAdmin && (
            <Link
              to={`/calibration?featureFormattedId=${encodeURIComponent(id)}&team=${encodeURIComponent(teamNumber ?? feature.teamNumber ?? '')}`}
              data-testid="calibrate-link"
              className="ids-btn ids-btn--secondary ids-btn--m"
              style={{ textDecoration: 'none' }}
            >
              Calibrate this feature
            </Link>
          )}
          {overallScore > 0 && (
            <span style={{ fontSize: '28px', fontWeight: 700, color: scoreColor(overallScore) }}>
              {overallScore}
            </span>
          )}
          {normalizedLevel && (
            <span className={levelBadgeClass(normalizedLevel)}>
              {levelLabel(normalizedLevel)}
            </span>
          )}
        </div>
      </div>

      {/* Recommendations */}
      <div data-testid="findings-recommendations" className="mb-lg">
        <RecommendationsPanel recommendations={feature.recommendations} dims={dimsRaw} />
      </div>

      {/* AC-8.1: 6 DimensionCards — show confidence now that Bedrock returns per-dim confidence.
          3 currently render as "Coming Soon" (see COMING_SOON_DISCOVERY_DIMENSIONS). */}
      <h3 className="text-lg font-semibold mb-md">Dimensions</h3>
      <div className="grid grid-cols-3 gap-md mb-lg" data-testid="dimension-cards">
        {allDimensionRecords.map((record) => (
          <DimensionCard
            key={record.dimension}
            record={record}
            showConfidence={true}
            comingSoon={comingSoonDimensionSet.has(record.dimension)}
            weightPercent={DIMENSION_WEIGHT_PERCENT[record.dimension]}
            onOpenRubric={openRubric}
          />
        ))}
      </div>
      <DimensionRubricDialog
        dimensions={allDimensionRecords.map((record) => record.dimension)}
        activeDimension={activeDimension}
        phaseLabel="Discovery"
        onClose={closeRubric}
        onNavigate={openRubric}
      />

      {/* Score History — moved to bottom; chart when 2+ scans, placeholder when first scan only */}
      <div className="mb-lg">
        <h3 className="text-lg font-semibold mb-md">Score History</h3>
        {scanHistoryArr.length > 1 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--genai-sem-surface-raised)', border: '1px solid var(--genai-sem-border)', borderRadius: '6px' }}
                labelStyle={{ color: 'var(--genai-sem-text-secondary)', fontSize: 11 }}
                itemStyle={{ fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="overall" name="Overall" stroke="var(--genai-sem-accent, #117d9b)" strokeWidth={3} dot={false} />
              {Object.entries(DIM_COLORS).map(([dim, color]) => (
                <Line key={dim} type="monotone" dataKey={dim} name={dim} stroke={color} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-secondary">
            Score trend will appear after this feature is scanned more than once. Re-scan to start tracking improvement over time.
          </p>
        )}
      </div>

      {/* Re-scan progress + messages — mirrors Delivery RepoDrillDown */}
      <RescanFooter
        progress={rescanArn ? <DiscoveryScanProgress executionArn={rescanArn} onComplete={handleRescanComplete} /> : null}
        notice={rescanNotice}
        noticeTestId="feature-rescan-notice"
        error={rescanError}
        errorTestId="feature-rescan-error"
        footer={
          teamNumber ? (
            <ScanFooterLine
              lastScannedAt={scanTimestamp || null}
              busy={rescanning}
              onScan={handleRescan}
              testId="feature-rescan"
              ariaLabel="Re-scan this feature"
              align="right"
            />
          ) : null
        }
      />
    </div>
  );
}
